import { Seo } from '@/components/Seo';
import { JsonLd, makeHowToSchema, makeFAQSchema, makeBreadcrumbSchema } from '@/components/Schema';
import { FAQ } from '@/components/FAQ';
import { FinalCTA } from '@/components/FinalCTA';
import { Link } from 'react-router-dom';
import { Search, List, Layout, FileCheck, SearchIcon } from 'lucide-react';

const steps = [
  'Choose a single-column ATS-friendly template.',
  'Fill in standard sections like Experience, Education, and Skills.',
  'Use keywords from the job description in your bullets.',
  'Download a clean PDF that parses easily in applicant tracking systems.',
];

const faqs = [
  {
    question: 'What makes a resume ATS-friendly?',
    answer: 'An ATS-friendly resume uses standard section headings, a single-column layout, common fonts, and minimal graphics. This helps parsing software read your content correctly.',
  },
  {
    question: 'Do your templates work with ATS?',
    answer: 'Yes. All ai-free-resume templates are single-column and use standard fonts and headings designed for ATS compatibility.',
  },
  {
    question: 'Should I include keywords from the job description?',
    answer: 'Yes. Including relevant keywords naturally in your experience and skills sections can improve your match score in many ATS systems.',
  },
  {
    question: 'What fonts should I use?',
    answer: 'Stick to widely supported fonts like Arial, Calibri, Georgia, Garamond, or Times New Roman. Our templates use ATS-safe fonts.',
  },
  {
    question: 'Are multi-column templates ATS-friendly?',
    answer: 'Multi-column layouts can confuse some ATS parsers. That is why we only offer single-column templates.',
  },
];

export function AtsResumeBuilder() {
  return (
    <>
      <Seo
        title="ATS Resume Builder | ATS-Friendly Templates, Free PDF"
        description="Build an ATS-friendly resume with single-column templates and standard fonts. No sign-up required. Free PDF download."
        path="/ats-resume-builder"
      />
      <JsonLd schema={makeHowToSchema('How to build an ATS-friendly resume', 'Create a resume that parses cleanly in applicant tracking systems.', steps)} />
      <JsonLd schema={makeFAQSchema(faqs)} />
      <JsonLd schema={makeBreadcrumbSchema([{ name: 'ATS Resume Builder', path: '/ats-resume-builder' }])} />

      <section className="hero-glow py-24 md:py-32">
        <div className="mx-auto max-w-[800px] px-6 text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-[#00D4AA]/15">
            <Search size={32} className="text-[#00D4AA]" />
          </div>
          <h1 className="text-balance mt-6 text-4xl font-extrabold text-white md:text-5xl">
            ATS Resume Builder — Beat the Applicant Tracking System
          </h1>
          <p className="mx-auto mt-6 max-w-[680px] text-lg text-white/75">
            An ATS-friendly resume uses single-column layout, standard fonts, and no graphics. Our ATS resume builder formats your resume so it can be parsed by applicant tracking systems.
          </p>
          <Link
            to="/app/create"
            className="btn-primary mt-8 inline-flex items-center justify-center rounded-[10px] px-8 py-4 text-base font-semibold"
          >
            Build ATS Resume
          </Link>
        </div>
      </section>

      <section className="bg-[#0A2540] py-16 md:py-24">
        <div className="mx-auto max-w-[1200px] px-6">
          <div className="text-center">
            <span className="rounded-full bg-[#00D4AA]/15 px-4 py-1.5 text-sm font-semibold text-[#00D4AA]">
              ATS checklist
            </span>
            <h2 className="mt-4 text-3xl font-bold text-white md:text-4xl">
              Built for ATS compatibility
            </h2>
          </div>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { icon: List, title: 'Standard Headings', desc: 'Experience, Education, and Skills sections use headings ATS parsers recognize.' },
              { icon: Layout, title: 'Single Column', desc: 'One-column layouts avoid the parsing errors common in multi-column designs.' },
              { icon: FileCheck, title: 'No Hidden Graphics', desc: 'No icons, charts, or graphics embedded in the text flow that could confuse parsers.' },
              { icon: SearchIcon, title: 'Keyword Friendly', desc: 'Clean structure makes it easy to add the keywords recruiters search for.' },
            ].map((f) => (
              <div key={f.title} className="glass-card rounded-2xl p-6 transition duration-300 hover:-translate-y-1 hover:border-[rgba(0,212,170,0.35)]">
                <div className="flex h-12 w-12 items-center justify-center rounded-[10px] bg-[#00D4AA]/15">
                  <f.icon size={24} className="text-[#00D4AA]" />
                </div>
                <h3 className="mt-4 text-lg font-semibold text-white">{f.title}</h3>
                <p className="mt-2 text-[15px] text-white/75">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-20 md:py-28">
        <div className="mx-auto max-w-[1200px] px-6">
          <div className="text-center">
            <span className="rounded-full bg-[#E7F1FA] px-4 py-1.5 text-sm font-semibold text-[#0A2540]">
              How to build
            </span>
            <h2 className="mt-4 text-3xl font-bold text-[#0A2540] md:text-4xl">
              Build an ATS-friendly resume in 4 steps
            </h2>
          </div>
          <div className="mt-12 grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            {steps.map((s, i) => (
              <div key={i} className="relative rounded-2xl border border-[#E2E8F0] bg-[#F8FAFC] p-6 transition duration-300 hover:bg-white hover:shadow-sm">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#0A2540] text-base font-bold text-white">
                  {i + 1}
                </div>
                <p className="mt-4 text-[#4A5568]">{s}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#F6F9FC] py-16">
        <div className="mx-auto max-w-[800px] px-6">
          <div className="text-center">
            <span className="rounded-full bg-[#E7F1FA] px-4 py-1.5 text-sm font-semibold text-[#0A2540]">
              FAQ
            </span>
            <h2 className="mt-4 text-3xl font-bold text-[#0A2540]">
              ATS Resume Builder FAQ
            </h2>
          </div>
          <div className="mt-10">
            <FAQ items={faqs} />
          </div>
        </div>
      </section>

      <FinalCTA
        title="Build an ATS-friendly resume"
        subtitle="Free to use. No sign-up required."
        buttonText="Create ATS Resume"
        buttonTo="/app/create"
      />
    </>
  );
}

export default AtsResumeBuilder;
